import { useCallback } from "react";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { ModalShell } from "../../../atoms/ModalShell";
import { AiOutlineLoading } from "react-icons/ai";
import { useDropzone } from "react-dropzone";
import { queryClient } from "../../../../providers/QueryProvider";
import { useAddUserAutomatically } from "../../../../hooks/users";
import {
  HiDocumentAdd,
  HiCloudDownload,
  HiInformationCircle,
} from "react-icons/hi";

const AddUserAutomaticallyModal = ({
  show,
  onHide,
  size,
  className,
  onBackClick,
}: {
  show: boolean;
  onHide: () => void;
  size?: "lg" | "sm";
  className?: string | undefined;
  onBackClick: () => void;
}) => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const canImportUsers = permissions.can(Cap.IMPORT_USERS, Level.WRITE);

  const { mutate: addUsersAuto, isLoading: isAddingAuto } =
    useAddUserAutomatically();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!canImportUsers) return;
      addUsersAuto(
        {
          customer_identification: customer_identification,
          entityIdentification: user.entity_identification,
          data: { file: acceptedFiles[0] },
        },
        {
          onSuccess: () => {
            onHide();
          },
        }
      );
    },
    [addUsersAuto, customer_identification, onHide, queryClient, canImportUsers]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
    maxSize: 5242880 * 2,
  });

  return (
    <ModalShell
      show={show}
      onClose={onHide}
      size={size}
      className={className}
      title={t("dashboard.addUserAutomatically.title")}
      subtitle={t("dashboard.addUserAutomatically.text")}
    >
      <AlertMessage color="info" icon={HiInformationCircle} className="mb-4">
        <span>
          {t("dashboard.addUserAutomatically.emailValidationNoticePrefix")}
          <Link
            to="/control-panel/settings"
            state={{ section: "cleanDatabase" }}
            onClick={onHide}
            className="underline font-semibold hover:text-blue-700"
          >
            {t(
              "dashboard.addUserAutomatically.emailValidationNoticeSettingsLink"
            )}
          </Link>
          {t("dashboard.addUserAutomatically.emailValidationNoticeSuffix")}
        </span>
      </AlertMessage>

      <div
        className="w-full px-6 py-20 rounded-md border-2 border-gray-300 border-dashed flex-col justify-start items-center gap-1 inline-flex mb-6"
        {...getRootProps()}
      >
        <div className="w-12 h-12 relative">
          <HiDocumentAdd size={48} className="text-gray-400" />
        </div>
        <input {...getInputProps()} />
        <div className="justify-start items-center gap-1 inline-flex">
          <div className="bg-white rounded-md justify-start items-center flex">
            <div className="text-blue-600 text-sm font-semibold font-figtree leading-tight">
              {t("dashboard.addUserAutomatically.key1")}
            </div>
          </div>
          <div className="text-gray-600 text-sm font-normal font-figtree leading-tight">
            {t("dashboard.addUserAutomatically.key2")}
          </div>
        </div>
        <div className="text-center text-gray-500 text-xs font-normal font-figtree leading-none">
          {t("dashboard.addUserAutomatically.key3")}
        </div>
        {isAddingAuto && (
          <div className="text-center">
            <AiOutlineLoading className="h-6 w-6 animate-spin" />
          </div>
        )}
      </div>

      <div className="justify-center items-start gap-2 flex">
        <div className="w-5 h-5 relative">
          <HiCloudDownload size={20} className="text-blue-500" />
        </div>
        <a
          href="/sample.csv"
          download
          className="text-blue-600 text-sm font-semibold font-figtree leading-tight"
        >
          {t("dashboard.addUserAutomatically.title-01")}
        </a>
      </div>
    </ModalShell>
  );
};

export default AddUserAutomaticallyModal;
